'use client';

import Image from 'next/image';
import styles from './DeviceShowcaseSection.module.scss';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { PaintDrip } from '../PaintDrip/PaintDrip';

interface Device {
  type: 'laptop' | 'phone' | 'tablet';
  screenshot: string;
  alt: string;
}

interface DeviceShowcaseSectionProps {
  label?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  devices?: Device[];
  backgroundImage?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

const DEFAULT_DEVICES: Device[] = [
  {
    type: 'phone',
    screenshot:
      'https://ik.imagekit.io/lindahestia/articles/smartphone_executive_portal_transparent_under_100kb.webp?updatedAt=1780318308497',
    alt: 'Application mobile executive portal',
  },
  {
    type: 'laptop',
    screenshot:
      'https://ik.imagekit.io/lindahestia/articles/laptop_corporate_data_transparent_under_100kb.webp?updatedAt=1780318308551',
    alt: 'Application corporate data sur laptop',
  },
  {
    type: 'tablet',
    screenshot:
      'https://ik.imagekit.io/lindahestia/articles/tablet_client_facing_transparent_under_100kb.webp?updatedAt=1780318308530',
    alt: 'Application tablette client facing',
  },
];

export function DeviceShowcaseSection({
  label = 'Excellence technique',
  title = 'Des applications',
  highlightedTitle = 'performantes.',
  description = "Nous livrons des solutions web et mobile optimisées pour la vitesse, l'accessibilité et le référencement — avec des scores Lighthouse parfaits.",
  devices = DEFAULT_DEVICES,
  backgroundImage,
  ctaLabel,
  ctaHref,
}: DeviceShowcaseSectionProps) {
  useAOS();

  const effectiveDevices = devices?.length ? devices : DEFAULT_DEVICES;
  const phone = effectiveDevices.find((d) => d.type === 'phone') ?? DEFAULT_DEVICES[0];
  const laptop = effectiveDevices.find((d) => d.type === 'laptop') ?? DEFAULT_DEVICES[1];
  const tablet = effectiveDevices.find((d) => d.type === 'tablet') ?? DEFAULT_DEVICES[2];

  return (
    <section
      className={styles.section}
      aria-labelledby="showcase-label"
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.overlay} aria-hidden="true" />

      {/* Diagonal coloured banner */}
      <div className={styles.diagonalBanner} aria-hidden="true" />

      {/* Paint drip leaking from hero into this section */}
      <PaintDrip style={{ top: '-120px' }} />

      <div className={styles.grid}>
        {/* Left — Devices cluster */}
        <div className={styles.left}>
          <div
            className={styles.cluster}
            data-aos="fade-up"
            data-aos-duration="900"
            data-aos-delay="100"
            data-aos-easing="ease-out-cubic"
          >
            {/* Laptop — back / centre / elevated / larger */}
            {laptop && (
              <div
                className={`${styles.device} ${styles.laptop}`}
                data-aos="fade-up"
                data-aos-duration="900"
                data-aos-delay="0"
                data-aos-easing="ease-out-cubic"
              >
                <Image
                  src={laptop.screenshot}
                  alt={laptop.alt}
                  fill
                  sizes="(max-width: 968px) 80vw, 560px"
                  className={styles.deviceImage}
                  loading="lazy"
                />
              </div>
            )}

            {/* Phone — front / bottom-left */}
            {phone && (
              <div
                className={`${styles.device} ${styles.phone}`}
                data-aos="fade-up"
                data-aos-duration="1000"
                data-aos-delay="200"
                data-aos-easing="ease-out-cubic"
              >
                <Image
                  src={phone.screenshot}
                  alt={phone.alt}
                  fill
                  sizes="150px"
                  className={styles.deviceImage}
                  loading="lazy"
                />
              </div>
            )}

            {/* Tablet — front / bottom-right */}
            {tablet && (
              <div
                className={`${styles.device} ${styles.tablet}`}
                data-aos="fade-up"
                data-aos-duration="700"
                data-aos-delay="100"
                data-aos-easing="ease-out-cubic"
              >
                <Image
                  src={tablet.screenshot}
                  alt={tablet.alt}
                  fill
                  sizes="260px"
                  className={styles.deviceImage}
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </div>

        {/* Right — Text */}
        <div className={styles.right}>
          <p
            id="showcase-label"
            className={styles.sectionLabel}
            data-aos="fade"
            data-aos-duration="200"
          >
            {label}
          </p>
          <h2 className={styles.title}>
            {title} <em>{highlightedTitle}</em>
          </h2>
          <p className={styles.description}>{description}</p>

          {ctaLabel && ctaHref && (
            <a
              href={ctaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaLabel, 'device_showcase_cta', ctaHref)}
            >
              {ctaLabel}
              <span aria-hidden="true">→</span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
